import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#setupThumbnailEdgeArrows();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#thumbnailResizeObserver?.disconnect();
  }

  /** @type {ResizeObserver | null} */
  #thumbnailResizeObserver = null;

  /**
   * Shows a thumbnail direction only when the first or last item is actually clipped.
   * Geometry is used instead of scrollLeft because padding and fractional iOS scroll
   * values can report movement while the edge thumbnail is still completely visible.
   */
  #setupThumbnailEdgeArrows() {
    const controls = this.querySelector('slideshow-controls[thumbnails]');
    const viewport = controls?.querySelector('.slideshow-controls__thumbnails-container');
    const thumbnails = controls?.querySelectorAll('.slideshow-controls__thumbnail');
    const arrows = controls?.querySelectorAll(':scope > .slideshow-controls__arrows');
    if (!(viewport instanceof HTMLElement) || !thumbnails?.length || !arrows || arrows.length < 2) return;

    const previous = arrows[0];
    const next = arrows[arrows.length - 1];
    const first = thumbnails[0];
    const last = thumbnails[thumbnails.length - 1];
    const tolerance = 1.5;

    const update = () => {
      const viewportRect = viewport.getBoundingClientRect();
      const firstRect = first.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      const vertical = controls.matches('[pagination-position="left"], [pagination-position="right"]') &&
        window.matchMedia('(min-width: 750px)').matches;
      const firstClipped = vertical
        ? firstRect.top < viewportRect.top - tolerance
        : firstRect.left < viewportRect.left - tolerance;
      const lastClipped = vertical
        ? lastRect.bottom > viewportRect.bottom + tolerance
        : lastRect.right > viewportRect.right + tolerance;

      previous.toggleAttribute('data-thumbnail-edge-hidden', !firstClipped);
      next.toggleAttribute('data-thumbnail-edge-hidden', !lastClipped);
      this.setAttribute('data-thumbnail-arrows-ready', '');
    };

    let frame = 0;
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    viewport.addEventListener('scroll', scheduleUpdate, { passive: true, signal: this.#controller.signal });
    this.#thumbnailResizeObserver = new ResizeObserver(scheduleUpdate);
    this.#thumbnailResizeObserver.observe(viewport);
    this.#thumbnailResizeObserver.observe(first);
    this.#thumbnailResizeObserver.observe(last);
    requestAnimationFrame(update);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
